import {
  DEFAULT_OPPORTUNITY_FILTERS,
  DEFAULT_SETTINGS,
  type Settings,
} from "../types/settings";

type LegacyStoredSettings = Partial<Settings> & {
  exchanges?: Record<string | number, boolean>;
};

function migrateSpotFuturesGapMagnitude(
  value: number | undefined,
  sourceVersion: number | undefined,
): number {
  if (value === undefined) {
    return DEFAULT_SETTINGS.hedgeConfig.spotFuturesGapMagnitude;
  }

  if ((sourceVersion ?? 1) < 2 && value > 0) {
    return -value;
  }

  return value;
}

export interface HedgeRebalancingUpdate {
  price_surge_threshold?: number;
  liquidation_proximity_threshold?: number;
  entry_funding_rate_threshold?: number | null;
  entry_funding_interval_hours?: number;
  spot_futures_gap_magnitude_ratio?: number;
  low_funding_rate_threshold?: number;
  funding_time_window_seconds?: number;
}

export function mergeStoredSettings(
  stored: LegacyStoredSettings | null | undefined,
): Settings {
  const parsed = stored ?? {};
  const legacyExchanges = parsed.exchanges;
  const buyExchanges =
    parsed.buyExchanges ?? legacyExchanges ?? DEFAULT_SETTINGS.buyExchanges;
  const sellExchanges =
    parsed.sellExchanges ?? legacyExchanges ?? DEFAULT_SETTINGS.sellExchanges;
  const sourceVersion = parsed.version;

  return {
    ...DEFAULT_SETTINGS,
    ...parsed,
    version: DEFAULT_SETTINGS.version,
    filters: {
      ...DEFAULT_SETTINGS.filters,
      ...parsed.filters,
      networkStatus: {
        ...DEFAULT_SETTINGS.filters.networkStatus,
        ...parsed.filters?.networkStatus,
      },
      tradeAmountUSD:
        parsed.filters?.tradeAmountUSD ??
        DEFAULT_SETTINGS.filters.tradeAmountUSD,
    },
    opportunityFilters: {
      ...DEFAULT_OPPORTUNITY_FILTERS,
      ...parsed.opportunityFilters,
      exchangeFilters: {
        ...DEFAULT_OPPORTUNITY_FILTERS.exchangeFilters,
        ...parsed.opportunityFilters?.exchangeFilters,
      },
    },
    buyExchanges: {
      ...DEFAULT_SETTINGS.buyExchanges,
      ...buyExchanges,
    },
    sellExchanges: {
      ...DEFAULT_SETTINGS.sellExchanges,
      ...sellExchanges,
    },
    display: {
      ...DEFAULT_SETTINGS.display,
      ...parsed.display,
    },
    features: {
      ...DEFAULT_SETTINGS.features,
      ...parsed.features,
    },
    strategyBudget: parsed.strategyBudget ?? DEFAULT_SETTINGS.strategyBudget,
    leverage: parsed.leverage ?? DEFAULT_SETTINGS.leverage,
    useMultiTierProfit:
      parsed.useMultiTierProfit ?? DEFAULT_SETTINGS.useMultiTierProfit,
    profitTiers: parsed.profitTiers ?? DEFAULT_SETTINGS.profitTiers,
    entryDiffPercent:
      parsed.entryDiffPercent ?? DEFAULT_SETTINGS.entryDiffPercent,
    hedgeConfig: {
      ...DEFAULT_SETTINGS.hedgeConfig,
      ...parsed.hedgeConfig,
      spotFuturesGapMagnitude: migrateSpotFuturesGapMagnitude(
        parsed.hedgeConfig?.spotFuturesGapMagnitude,
        sourceVersion,
      ),
    },
    buyOpenMode: parsed.buyOpenMode ?? DEFAULT_SETTINGS.buyOpenMode,
    marketNotionalUsdt:
      parsed.marketNotionalUsdt ?? DEFAULT_SETTINGS.marketNotionalUsdt,
    replayEnabled: parsed.replayEnabled ?? DEFAULT_SETTINGS.replayEnabled,
    replayDelaySeconds:
      parsed.replayDelaySeconds ?? DEFAULT_SETTINGS.replayDelaySeconds,
  };
}

export function applyReplaySettings(
  settings: Settings,
  replayEnabled: boolean,
  replayDelaySeconds: number,
): Settings {
  return {
    ...settings,
    replayEnabled,
    replayDelaySeconds,
  };
}

export function applyHedgeConfigUpdate(
  settings: Settings,
  leverage?: number,
  rebalancing?: HedgeRebalancingUpdate,
): Settings {
  return {
    ...settings,
    leverage: leverage ?? settings.leverage,
    hedgeConfig: {
      ...settings.hedgeConfig,
      ...(rebalancing?.price_surge_threshold !== undefined
        ? {
            priceSurgeThreshold: rebalancing.price_surge_threshold * 100,
          }
        : {}),
      ...(rebalancing?.liquidation_proximity_threshold !== undefined
        ? {
            liquidationProximity:
              rebalancing.liquidation_proximity_threshold * 100,
          }
        : {}),
      ...(rebalancing?.entry_funding_rate_threshold !== undefined
        ? {
            entryFundingRate:
              rebalancing.entry_funding_rate_threshold === null
                ? null
                : rebalancing.entry_funding_rate_threshold * 100,
          }
        : {}),
      ...(rebalancing?.entry_funding_interval_hours !== undefined
        ? {
            entryFundingIntervalHours:
              rebalancing.entry_funding_interval_hours as 0 | 1 | 4 | 8,
          }
        : {}),
      ...(rebalancing?.spot_futures_gap_magnitude_ratio !== undefined
        ? {
            spotFuturesGapMagnitude:
              rebalancing.spot_futures_gap_magnitude_ratio * 100,
          }
        : {}),
      ...(rebalancing?.low_funding_rate_threshold !== undefined
        ? {
            lowFundingRate: rebalancing.low_funding_rate_threshold * 100,
          }
        : {}),
      ...(rebalancing?.funding_time_window_seconds !== undefined
        ? {
            fundingTimeWindow: rebalancing.funding_time_window_seconds,
          }
        : {}),
    },
  };
}
